import type { QueryClient } from '@tanstack/react-query';
import { accountHasFeatureFlag, type AccountFeatureFlag } from '@/services/auth/feature-flags';
import { accountStore } from '@/services/auth/store';
import { queryClient } from '@/services/query-client';
import { sentryAddAppLoadBreadcrumb } from '@/utils/sentry';
import { prefetchForPath } from './prefetch';
import { isHydraRoute } from './route-ownership';

/**
 * Warming a page this app owns while the user is still standing on a page the
 * embedded app owns.
 *
 * A page migrated one tab at a time leaves the user on an embedded-app tab with
 * a link to a tab this app serves. Clicking it pays for the route chunk and the
 * page's first fetch at once, with nothing on screen to cover either. The
 * addresses of both sides are known before the click, so both costs can be paid
 * while the user is still reading the tab they are on.
 *
 * Entries are registered by the routes layer, which is where knowledge of any
 * particular product area belongs. This module only knows about URLs.
 */

export type SiblingPreloadContext = {
  queryClient: QueryClient;
  /** The sibling being preloaded. */
  href: string;
  /** The match against the entry's `when`, so callers can reuse its captures. */
  match: RegExpMatchArray;
};

export type SiblingPreloadEntry = {
  /** The address the user is on — an address the embedded app owns. */
  when: RegExp;
  /**
   * The sibling page's rollout flag. Nothing is preloaded while it is off for
   * the account. Route ownership is checked as well, since it is what decides
   * which app answers the click, but the flag is the gate a reader looks for.
   */
  flag?: AccountFeatureFlag;
  /** Concrete hrefs this app may own, built from the `when` match. */
  siblings: (match: RegExpMatchArray) => string[];
  /**
   * Warm the data the sibling page reads. Runs only after the sibling is
   * confirmed to be Hydra-owned for this account, so it cannot fire for an
   * account whose rollout flag is off.
   */
  // oxlint-disable-next-line local-rules/no-unknown-returns -- nothing reads the result; narrowing to Promise<void> would reject an entry that returns a typed prefetch promise directly
  prefetchData?: (context: SiblingPreloadContext) => Promise<unknown>;
};

const registry: SiblingPreloadEntry[] = [];

/** Hrefs already preloaded (or in flight). A failed attempt is removed so it can retry. */
const preloaded = new Set<string>();

export function registerSiblingPreload(entry: SiblingPreloadEntry) {
  registry.push(entry);
}

function scheduleWhenIdle(callback: () => void) {
  if (typeof globalThis.requestIdleCallback === 'function') {
    // The timeout matters more than the idle window: the user may click within
    // a second of landing, and a preload that never ran is worse than one that
    // competed briefly with the embedded app's own work.
    globalThis.requestIdleCallback(
      () => {
        callback();
      },
      { timeout: 2000 },
    );
    return;
  }
  globalThis.setTimeout(callback, 0);
}

function runPreload(entry: SiblingPreloadEntry, href: string, match: RegExpMatchArray) {
  performance.mark(`hydra:sibling-preload:${href}`);
  sentryAddAppLoadBreadcrumb('sibling preload started', { href });

  prefetchForPath(href);

  const data = entry.prefetchData?.({ queryClient, href, match });
  if (data == null) return;

  void data
    .then(() => {
      sentryAddAppLoadBreadcrumb('sibling preload data resolved', { href });
    })
    .catch((error: unknown) => {
      // Nothing is on screen waiting for this, so a failure is only worth a
      // breadcrumb — and worth forgetting, so the click path retries.
      preloaded.delete(href);
      sentryAddAppLoadBreadcrumb('sibling preload data failed', { href, error: String(error) }, 'warning');
    });
}

function preloadSibling(entry: SiblingPreloadEntry, href: string, match: RegExpMatchArray, pathname: string) {
  if (href === pathname) return;
  if (preloaded.has(href)) return;
  // Both reads answer "off"/`embedded` until the account lands, so this is
  // deliberately not memoised: the caller re-runs once the flags are known.
  const account = accountStore.get();
  if (entry.flag != null && !accountHasFeatureFlag(account, entry.flag)) return;
  if (!isHydraRoute(href, account)) return;

  preloaded.add(href);
  scheduleWhenIdle(() => {
    try {
      runPreload(entry, href, match);
    } catch (error: unknown) {
      preloaded.delete(href);
      sentryAddAppLoadBreadcrumb('sibling preload failed', { href, error: String(error) }, 'warning');
    }
  });
}

/**
 * Preload every Hydra-owned sibling registered for `pathname`. Safe to call on
 * every location change and whenever the account changes; it never throws.
 */
export function preloadSiblingsForPath(pathname: string) {
  for (const entry of registry) {
    const match = pathname.match(entry.when);
    if (match == null) continue;

    for (const href of entry.siblings(match)) {
      preloadSibling(entry, href, match, pathname);
    }
  }
}

/** Reset all module state. Test-only — not for production use. */
export function resetSiblingPreloadState() {
  registry.length = 0;
  preloaded.clear();
}
